<template>
    <div></div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { pinia } from '@/pinia';
import { useUserStore } from '@/stores/user';
import { useSocketStore } from '@/stores/socket';
import { useWalletsStore } from '@/stores/wallets';
import { useOrdersStore } from '@/stores/orders';
import { useFuturesStore } from '@/stores/futures';
import { useMarketsStore } from '@/stores/markets';
import { useToast } from 'vue-toast-notification';
import { useI18n } from 'vue-i18n';
import { math_formatter } from '@/Functions/Math';

export default {
    name: 'PrivateChannel',

    setup() {
        const userStore = useUserStore(pinia);
        const socketStore = useSocketStore(pinia);
        const walletsStore = useWalletsStore(pinia);
        const ordersStore = useOrdersStore(pinia);
        const futuresStore = useFuturesStore(pinia);
        const marketsStore = useMarketsStore(pinia);
        const toast = useToast();
        const { t } = useI18n();

        const joinedChannel = ref(null);
        const channelInstance = ref(null);
        const hasWarned = ref(false);
        let subscriptionSucceededHandler = null;

        // ---- Duplicate broadcast guard -------------------------------------
        // The server can deliver the same broadcast more than once: several
        // lifecycle events are re-fired on some code paths (the backend itself
        // keeps replay guards for this, e.g. OnFuturesOrderFilledApplyToProp).
        // We drop any (event + payload) already handled within a short window.
        // This collapses the double toast AND prevents duplicate rows (e.g. the
        // settled-option trade that prependOptionTrade unshifts on every
        // delivery). Genuinely distinct events differ in payload — including
        // successive partial fills, which carry different filled_qty — so they
        // are never suppressed.
        //
        // The key must ignore broadcast-time-only fields: a payload is otherwise
        // expected to be deterministic, but some events stamp a fresh value on
        // every broadcast (e.g. OptionOrderPlaced sets server_ts => time()).
        // Those differ between re-deliveries and would defeat the guard, so we
        // strip them before building the key — this is what was letting the
        // "Option opened" toast fire twice.
        const VOLATILE_PAYLOAD_KEYS = ['server_ts'];
        const dedupeKeyPayload = (payload) => {
            if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
                return payload;
            }
            const stable = {};
            for (const k in payload) {
                if (!VOLATILE_PAYLOAD_KEYS.includes(k)) stable[k] = payload[k];
            }
            return stable;
        };
        const recentEvents = new Map(); // key -> last-seen timestamp (ms)
        const EVENT_DEDUPE_MS = 5000;
        const isDuplicateEvent = (name, payload) => {
            let key;
            try {
                key = name + '|' + JSON.stringify(dedupeKeyPayload(payload));
            } catch (_) {
                return false; // un-serializable payload: never suppress
            }
            const now = Date.now();
            const seenAt = recentEvents.get(key);
            if (seenAt != null && now - seenAt < EVENT_DEDUPE_MS) {
                recentEvents.set(key, now); // refresh so a 3rd copy is dropped too
                return true;
            }
            recentEvents.set(key, now);
            if (recentEvents.size > 100) {
                for (const [k, ts] of recentEvents) {
                    if (now - ts >= EVENT_DEDUPE_MS) recentEvents.delete(k);
                }
            }
            return false;
        };

        const user = computed(() => userStore.getUser);
        const channel = computed(() => userStore.getUserChannel);
        const sanitizeToastMessage = (message) => {
            return String(message ?? '').replace(/[\u0000-\u001F\u007F]/g, '').trim();
        };
        const safeToast = (fn, message, context = toast) => {
            const clean = sanitizeToastMessage(message);
            if (!clean) return;
            try {
                if (typeof fn !== 'function') {
                    throw new Error('Toast function unavailable');
                }
                fn.call(context, clean);
            } catch (err) {
                console.error('[PrivateChannel] Toast error:', err, clean);
            }
        };

        let boundPusherCh = null;

        const leaveChannel = () => {
            socketStore.setPrivateChannelReady(false);
            const privateKey = joinedChannel.value ? `private-${joinedChannel.value}` : null;
            if (boundPusherCh && subscriptionSucceededHandler) {
                try { boundPusherCh.unbind('pusher:subscription_succeeded', subscriptionSucceededHandler); } catch (_) {}
                boundPusherCh = null;
                subscriptionSucceededHandler = null;
            }
            if (channelInstance.value) {
                channelInstance.value.unsubscribe();
                channelInstance.value = null;
            }
            if (joinedChannel.value && window.Echo?.connector?.channels) {
                if (window.Echo.connector.channels[privateKey]) {
                    window.Echo.leave(joinedChannel.value);
                }
            }
            joinedChannel.value = null;
        };

        const tryJoin = () => {
            // Defensive: ensure user and channel are set and channel is a string
            if (!user.value?.id || !channel.value || typeof channel.value !== 'string') {
                // Only warn once, and only if user exists but channel is missing (unexpected case)
                // For guest users (user.value === null), this is expected behavior
                if (!hasWarned.value && user.value !== null) {
                    console.warn('[PrivateChannel] Cannot join: user or channel missing or invalid', user.value, channel.value);
                    hasWarned.value = true;
                }
                return;
            }
            
            // Reset warning flag when we have valid user/channel
            if (hasWarned.value) {
                hasWarned.value = false;
            }
            
            const baseChannel = channel.value.replace(/^private-/, '');

            // If already joined to this channel with a valid instance, do nothing
            if (joinedChannel.value === baseChannel && channelInstance.value &&
                window.Echo?.connector?.channels &&
                `private-${baseChannel}` in window.Echo.connector.channels) {
                return;
            }
            
            // If joined to a different channel, leave it first
            if (joinedChannel.value && joinedChannel.value !== baseChannel) {
                leaveChannel();
            }
            try {
                const privateKey = `private-${baseChannel}`;
                channelInstance.value = window.Echo.private(baseChannel);

                subscriptionSucceededHandler = () => {
                    socketStore.setPrivateChannelReady(true);
                };
                const pusherCh = window.Echo.connector?.pusher?.channel?.(privateKey);
                if (pusherCh && pusherCh.bind) {
                    pusherCh.bind('pusher:subscription_succeeded', subscriptionSucceededHandler);
                    boundPusherCh = pusherCh;
                    if (pusherCh.subscribed) {
                        socketStore.setPrivateChannelReady(true);
                    } else {
                        // Safety: if subscription_succeeded never fires, check after 5s
                        setTimeout(() => {
                            if (joinedChannel.value === baseChannel && !socketStore.getPrivateChannelReady) {
                                const ch = window.Echo?.connector?.pusher?.channel?.(privateKey);
                                if (ch?.subscribed) {
                                    socketStore.setPrivateChannelReady(true);
                                }
                            }
                        }, 5000);
                    }
                } else {
                    setTimeout(() => socketStore.setPrivateChannelReady(true), 300);
                }

                // Add event listeners
                channelInstance.value
                    .listen('.WalletUpdated', (payload) => {
                        walletsStore.updateWallet({ wallet: payload.wallet });
                    })
                    .listen('.FuturesAccountUpdated', (payload) => {
                        walletsStore.updateFutureWallet({ futureAccount: payload.account });
                        futuresStore.handleAccountUpdated(payload);
                    })
                    .listen('.FuturesPositionUpdated', (payload) => {
                        ordersStore.handleFuturesPositionUpdated(payload);
                        futuresStore.handlePositionUpdated(payload);
                    })
                    .listen('.FuturesOrderCreated', (payload) => {
                        if (isDuplicateEvent('FuturesOrderCreated', payload)) return;
                        ordersStore.handleFuturesOrderCreated(payload);
                        futuresStore.handleOrderCreated(payload);
                        // Market orders fill immediately; the .FuturesOrderFilled toast is the
                        // meaningful confirmation, so skip "created" to avoid a double toast.
                        if (payload?.order?.type !== 'market') {
                            safeToast(toast.success, t('common.socket.futures_order_created'));
                        }
                    })
                    .listen('.FuturesOrderUpdated', (payload) => {
                        ordersStore.handleFuturesOrderUpdated(payload);
                        futuresStore.handleOrderUpdated(payload);
                    })
                    .listen('.FuturesOrderPartiallyFilled', (payload) => {
                        if (isDuplicateEvent('FuturesOrderPartiallyFilled', payload)) return;
                        ordersStore.handleFuturesOrderPartiallyFilled(payload);
                        futuresStore.handleOrderUpdated(payload);
                        safeToast(toast.info, t('common.socket.futures_order_partially_filled'));
                    })
                    .listen('.FuturesOrderFilled', (payload) => {
                        if (isDuplicateEvent('FuturesOrderFilled', payload)) return;
                        ordersStore.handleFuturesOrderFilled(payload);
                        futuresStore.handleOrderFilled(payload);
                        safeToast(toast.success, t('common.socket.futures_order_filled'));
                    })
                    .listen('.FuturesOrderCancelled', (payload) => {
                        if (isDuplicateEvent('FuturesOrderCancelled', payload)) return;
                        ordersStore.handleFuturesOrderCancelled(payload);
                        futuresStore.handleOrderCancelled(payload);
                        safeToast(toast.info, t('common.socket.futures_order_cancelled'));
                    })
                    .listen('.NotifyUpdated', (payload) => {
                        if (payload?.notification) {
                            walletsStore.storeNotification({ notification: payload.notification });
                        }
                    })
                    .listen('.DepositUpdated', (payload) => {
                        if (payload.type === 'received') {
                            walletsStore.storeDeposit({ deposit: payload.deposit });
                        }
                        if (payload.type === 'updated') {
                            walletsStore.updateDeposit({ deposit: payload.deposit });
                        }
                    })
                    .listen('.WithdrawalUpdated', (payload) => {
                        walletsStore.updateWithdrawal({ withdrawal: payload.withdrawal });
                    })
                    .listen('.MarketTradePrivateUpdated', (payload) => {
                        if (isDuplicateEvent('MarketTradePrivateUpdated', payload)) return;
                        safeToast(
                            toast.success,
                            t('common.socket.order_processed_detail', {
                                rate: math_formatter(payload.trade.price, payload.market.quotePrecision),
                                quote: payload.market.quote,
                                quantity: math_formatter(payload.trade.quantity, payload.market.basePrecision),
                                base: payload.market.base,
                            })
                        );
                    })
                    .listen('.OrderCreated', (payload) => {
                        if (isDuplicateEvent('OrderCreated', payload)) return;
                        ordersStore.handleOrderCreated(payload);
                        safeToast(toast.success, t('common.socket.order_created'));
                    })
                    .listen('.OrderCancelled', (payload) => {
                        if (isDuplicateEvent('OrderCancelled', payload)) return;
                        ordersStore.handleOrderCancelled(payload);
                        if (payload?.order?.time_in_force === 'IOC') {
                            safeToast(toast.info, t('common.socket.ioc_order_cancelled'));
                        } else {
                            safeToast(toast.info, t('common.socket.order_cancelled'));
                        }
                    })
                    .listen('.OrderFilled', (payload) => {
                        if (isDuplicateEvent('OrderFilled', payload)) return;
                        ordersStore.handleOrderFilled(payload);
                        safeToast(toast.success, t('common.socket.order_filled'));
                    })
                    .listen('.OrderPartiallyFilled', (payload) => {
                        if (isDuplicateEvent('OrderPartiallyFilled', payload)) return;
                        ordersStore.handleOrderPartiallyFilled(payload);
                        safeToast(toast.info, t('common.socket.order_partially_filled'));
                    })
                    .listen('.OrderUpdated', (payload) => {
                        if (isDuplicateEvent('OrderUpdated', payload)) return;
                        ordersStore.handleOrderUpdated(payload);
                        safeToast(toast.info, t('common.socket.order_updated'));
                    })
                    .listen('.OptionsStatusUpdated', (payload) => {
                        if (isDuplicateEvent('OptionsStatusUpdated', payload)) return;
                        if (payload?.market && payload?.id) {
                            ordersStore.removeOptionFromOpenOrders(payload.market, payload.id);
                            marketsStore.prependOptionTrade(payload.market, payload);
                        }
                        if (payload?.status === 'won') {
                            safeToast(
                                toast.success,
                                t('common.socket.option_pnl_won', { pnl: payload.pnl, symbol: payload.symbol })
                            );
                        } else if (payload?.status === 'lost') {
                            safeToast(
                                toast.error,
                                t('common.socket.option_pnl_lost', { pnl: payload.pnl, symbol: payload.symbol })
                            );
                        }
                    })
                    .listen('.OptionOrderPlaced', (payload) => {
                        if (isDuplicateEvent('OptionOrderPlaced', payload)) return;
                        if (payload?.market && payload?.id) {
                            ordersStore.addOptionToOpenOrders(payload.market, payload);
                        }
                        const period = payload?.period_text ?? payload?.period ?? '?';
                        const dir = payload?.type === 'buy' ? t('common.socket.long') : t('common.socket.short');
                        const market = payload?.market ?? '';
                        safeToast(toast.info, t('common.socket.option_opened', { period, dir, market }));
                    })
                    .listen('.ChatMessageTyped', (payload) => {
                        if (window.VueWorker) {
                            window.VueWorker.emit('startTypingEvent', {
                                order: payload.order
                            });
                        }
                    })
                    .listen('.PeerOrderMessageSent', (payload) => {
                        if (window.VueWorker && payload?.order_id && payload?.message) {
                            window.VueWorker.emit('p2pChatNewMessage', {
                                order_id: payload.order_id,
                                message: payload.message
                            });
                        }
                    });
                
                joinedChannel.value = baseChannel;
            } catch (err) {
            }
        };

        onMounted(() => {
            if (socketStore.getSocket) {
                tryJoin();
            }
        });

        onBeforeUnmount(() => {
            leaveChannel();
        });

        // When socket connects/reconnects (null -> id), rejoin the private channel.
        // This replaces the old bindReconnect() which was bound to a specific pusher
        // instance and broke when Socket.vue recreated Echo.
        watch(() => socketStore.getSocket, (newSocket, oldSocket) => {
            if (newSocket && !oldSocket) {
                leaveChannel();
                tryJoin();
            } else if (!newSocket && oldSocket) {
                socketStore.setPrivateChannelReady(false);
            }
        });

        watch(user, (newUser, oldUser) => {
            if (newUser?.id !== oldUser?.id) {
                leaveChannel();
                tryJoin();
            }
        });

        watch(channel, (newChannel, oldChannel) => {
            if (newChannel !== oldChannel) {
                leaveChannel();
                tryJoin();
            }
        });

        return {
            user,
            channel
        };
    }
}
</script>
